import React, { useEffect, useState } from "react";
import { usePlatformAuth } from "context/PlatformAuthContext";
import { platformApiService } from "services/platformApi";
import { useNavigate } from "react-router-dom";

interface DashboardStats {
 stores: {
  total: number;
  active: number;
 };
 subscriptions: {
  total: number;
  active: number;
 };
 revenue: {
  total: number;
  platformFees: number;
  subscriptionRevenue: number;
 };
 invoices: {
  total: number;
  paid: number;
 };
}

const PlatformDashboard: React.FC = () => {
 const { user, isAuthenticated } = usePlatformAuth();
 const navigate = useNavigate();
 const [stats, setStats] = useState<DashboardStats | null>(null);
 const [loading, setLoading] = useState(true);

 useEffect(() => {
  if (!isAuthenticated) {
   navigate("/platform/login", { replace: true });
   return;
  }

  fetchDashboardStats();
 }, [isAuthenticated, navigate]);

 const fetchDashboardStats = async () => {
  try {
   const response = await platformApiService.getDashboardStats();
   setStats(response.data.stats);
  } catch (error) {
   console.error("Error fetching dashboard stats:", error);
  } finally {
   setLoading(false);
  }
 };

 if (loading) {
  return (
   <div className="p-8">
    <div className="text-center">Loading dashboard...</div>
   </div>
  );
 }

 return (
  <div className="p-8">
   <div className="mb-8">
    <h1 className="text-3xl font-bold text-gray-900">Platform Dashboard</h1>
    <p className="text-gray-600 mt-2">Welcome back, {user?.name}</p>
   </div>

   {stats && (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
     <div className="bg-white shadow p-6">
      <h3 className="text-sm font-medium text-gray-500">Total Stores</h3>
      <p className="text-3xl font-bold text-gray-900 mt-2">{stats.stores.total}</p>
      <p className="text-sm text-green-600 mt-1">{stats.stores.active} active</p>
     </div>

     <div className="bg-white shadow p-6">
      <h3 className="text-sm font-medium text-gray-500">Subscriptions</h3>
      <p className="text-3xl font-bold text-gray-900 mt-2">{stats.subscriptions.total}</p>
      <p className="text-sm text-green-600 mt-1">{stats.subscriptions.active} active</p>
     </div>

     <div className="bg-white shadow p-6">
      <h3 className="text-sm font-medium text-gray-500">Total Revenue</h3>
      <p className="text-3xl font-bold text-gray-900 mt-2">
       ${stats.revenue.total.toLocaleString()}
      </p>
      <p className="text-sm text-gray-600 mt-1">
       ${stats.revenue.platformFees.toLocaleString()} from fees
      </p>
     </div>

     <div className="bg-white shadow p-6">
      <h3 className="text-sm font-medium text-gray-500">Invoices</h3>
      <p className="text-3xl font-bold text-gray-900 mt-2">{stats.invoices.total}</p>
      <p className="text-sm text-green-600 mt-1">{stats.invoices.paid} paid</p>
     </div>
    </div>
   )}

   <div className="bg-white shadow p-6">
    <h2 className="text-xl font-semibold mb-4">Quick Actions</h2>
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
     <button
      onClick={() => navigate("/platform/stores")}
      className="p-4 border border-gray-300 hover:bg-gray-50 text-left"
     >
      <h3 className="font-medium">Manage Stores</h3>
      <p className="text-sm text-gray-600 mt-1">View and manage all stores</p>
     </button>
     <button
      onClick={() => navigate("/platform/subscriptions")}
      className="p-4 border border-gray-300 hover:bg-gray-50 text-left"
     >
      <h3 className="font-medium">Subscriptions</h3>
      <p className="text-sm text-gray-600 mt-1">Manage store subscriptions</p>
     </button>
     <button
      onClick={() => navigate("/platform/invoices")}
      className="p-4 border border-gray-300 hover:bg-gray-50 text-left"
     >
      <h3 className="font-medium">Invoices</h3>
      <p className="text-sm text-gray-600 mt-1">View all invoices</p>
     </button>
    </div>
   </div>
  </div>
 );
};

export default PlatformDashboard;





